// @ts-nocheck

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { Target } from '../../client';
import { __ } from '../../i18n/l10n';
import { Card } from '../../styled-components/dark/common';
import { MatchBetFiltersContext } from '../../utils/contexts';
import isLineUp from '../../utils/routes/isLineUp';
import { getAnchorIdByPathname } from '../../utils/scroll';
import { SMOOTH_SCROLL_TIMEOUT, smartScrollToRootAnchor } from '../../utils/smoothScroll';
import InfosLabel, { InfosLabelTypes } from '../InfosLabel';
import MatchGroupsContainer from '../MatchGroups/MatchGroupsContainer';
import MatchHotBetGroupsContainer from '../MatchHotBetGroups/MatchHotBetGroupsContainer';
import StickyMatchActionsRowContainer from '../StickyMatchActionsRow/StickyMatchActionsRowContainer';

const Wrapper = styled.div`
  ${({ $isFromLineUp }) =>
    $isFromLineUp && Target.__MOBILE__
      ? css`
          display: none;
        `
      : undefined}
`;

const StyledInfosLabel = styled(InfosLabel)`
  margin: 0.6em 0.6em 0em 0.6em;
  border-radius: ${Card.borderRadius};
`;

type MatchGroupsWithBetFiltersProps = {
  matchId: number;
  matchBetFilters?: Array<unknown>;
  matchHotPeriod?: number;
};

const MatchGroupsWithBetFilters = ({
  matchId,
  matchBetFilters = [],
  matchHotPeriod,
}: MatchGroupsWithBetFiltersProps) => {
  const { pathname: locationPathname } = useLocation();
  const isFromLineUp = useMemo(() => isLineUp(locationPathname), [locationPathname]);

  const [selectedFilterId, setSelectedFilterId] = useState(null);
  const [isHotFilterSelected, setIsHotFilterSelected] = useState(false);
  const [partialOnHotFilterClicked, setPartialOnHotFilterClicked] = useState(() => {});

  const onSelectionChanged = useCallback((filterId: string | null) => {
    setSelectedFilterId(filterId ?? null);
  }, []);

  const onHotFilterClicked = useCallback(() => {
    partialOnHotFilterClicked?.();
    setIsHotFilterSelected((prev) => !prev);
  }, [partialOnHotFilterClicked]);

  useEffect(() => {
    if (isHotFilterSelected) {
      const anchorId = getAnchorIdByPathname(locationPathname);

      if (anchorId) {
        smartScrollToRootAnchor(anchorId, SMOOTH_SCROLL_TIMEOUT, 0);
      }
    }
  }, [isHotFilterSelected, locationPathname]);

  const forwardedState = useMemo(
    () => ({
      filterId: selectedFilterId,
      setIsHotFilterSelected,
      isHotFilterSelected,
      onHotFilterClicked,
      setPartialOnHotFilterClicked,
    }),
    [selectedFilterId, isHotFilterSelected, onHotFilterClicked],
  );

  const hotInfoLabel =
    matchHotPeriod === 60
      ? __('View the most wagered bets in the last hour')
      : __('View the most wagered bets in the last %1$s mins', matchHotPeriod);

  return (
    <Wrapper $isFromLineUp={isFromLineUp}>
      <MatchBetFiltersContext.Provider value={forwardedState}>
        <StickyMatchActionsRowContainer
          matchId={matchId}
          filters={matchBetFilters}
          onFilterChanged={onSelectionChanged}
          defaultFilterId={isHotFilterSelected ? '' : undefined}
        />
        {isHotFilterSelected && matchHotPeriod ? (
          <StyledInfosLabel symbol={'i'} type={InfosLabelTypes.INFOS_BLUE} label={hotInfoLabel} />
        ) : null}
        {isHotFilterSelected ? (
          <MatchHotBetGroupsContainer />
        ) : (
          <MatchGroupsContainer matchId={matchId} filterId={selectedFilterId} />
        )}
      </MatchBetFiltersContext.Provider>
    </Wrapper>
  );
};

export default MatchGroupsWithBetFilters;
